import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  IconButton,
  Fab,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
  Tooltip,
  Collapse,
  Divider,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  ListItemText,
  Checkbox,
  Stack,
  Badge,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  ViewList as ListViewIcon,
  ViewModule as CardViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Star as StarIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DateRange as DateRangeIcon,
  AccessTime as TimeIcon,
  PlaylistAddCheck as BulkIcon,
} from '@mui/icons-material';
import { useStaffInfo } from '../../hooks/useStaffInfo';
import { StaffInfo, StaffRole, StaffStatus } from '../../types/staff';
import StaffEditor from './StaffEditor';

interface StaffListViewProps {
  onStaffSelect?: (staff: StaffInfo) => void;
}

type ViewMode = 'card' | 'table';

const StaffListView: React.FC<StaffListViewProps> = ({ onStaffSelect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // useStaffInfo hook integration
  const {
    filteredStaff,
    staffList,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    createStaff,
    updateStaff,
    deleteStaff,
    clearError,
  } = useStaffInfo({ autoLoad: true });

  // Local state
  const [viewMode, setViewMode] = useState<ViewMode>(isMobile ? 'card' : 'table');
  const [selectedStaff, setSelectedStaff] = useState<StaffInfo | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState<StaffInfo | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuStaffId, setMenuStaffId] = useState<string | null>(null);
  
  // Filter panel state
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [availableSkills, setAvailableSkills] = useState<string[]>([]);
  
  // Bulk operations state
  const [selectedStaffIds, setSelectedStaffIds] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Clear error when component mounts
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => clearError(), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  // Get available skills from all staff for filter dropdown
  useEffect(() => {
    if (staffList.length > 0) {
      const allSkills = new Set<string>();
      staffList.forEach(staff => {
        staff.qualifications.skills.forEach(skill => allSkills.add(skill));
      });
      setAvailableSkills(Array.from(allSkills).sort());
    }
  }, [staffList]);

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Handle view mode change
  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newViewMode: ViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Handle staff add
  const handleAddStaff = () => {
    setSelectedStaff(null);
    setEditorOpen(true);
  };

  // Handle staff edit
  const handleEditStaff = (staff: StaffInfo) => {
    setSelectedStaff(staff);
    setEditorOpen(true);
    handleMenuClose();
  };

  // Handle staff delete confirmation
  const handleDeleteStaff = (staff: StaffInfo) => {
    setStaffToDelete(staff);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Handle staff delete execution
  const handleConfirmDelete = async () => {
    if (staffToDelete) {
      const success = await deleteStaff(staffToDelete.id);
      if (success) {
        setDeleteDialogOpen(false);
        setStaffToDelete(null);
      }
    }
  };

  // Handle staff save (create/update)
  const handleStaffSave = async (staffData: StaffInfo) => {
    if (selectedStaff) {
      // Update existing staff
      await updateStaff(selectedStaff.id, staffData);
    } else {
      // Create new staff
      await createStaff(staffData);
    }
    setEditorOpen(false);
  };

  // Handle menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, staffId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuStaffId(staffId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuStaffId(null);
  };

  // Handle staff card click
  const handleStaffClick = (staff: StaffInfo) => {
    if (onStaffSelect) {
      onStaffSelect(staff);
    }
  };

  // Filter handlers
  const handleRoleFilter = (role: StaffRole | null) => {
    setFilters({ ...filters, role: role || undefined });
  };

  const handleStatusFilter = (status: StaffStatus | null) => {
    setFilters({ ...filters, status: status || undefined });
  };

  const handleSkillsFilter = (skills: string[]) => {
    setFilters({ ...filters, skills: skills.length > 0 ? skills : undefined });
  };

  const handleDateFilter = (date: Date | null) => {
    setFilters({ ...filters, availableDate: date || undefined });
  };

  const handleTimeRangeFilter = (timeRange: { start: string; end: string } | null) => {
    setFilters({ ...filters, availableTimeRange: timeRange || undefined });
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(value => 
    value !== undefined && value !== null && 
    (Array.isArray(value) ? value.length > 0 : true)
  ).length;

  // Bulk operations handlers
  const handleToggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedStaffIds(new Set());
  };

  const handleSelectStaff = (staffId: string, selected: boolean) => {
    const newSelection = new Set(selectedStaffIds);
    if (selected) {
      newSelection.add(staffId);
    } else {
      newSelection.delete(staffId);
    }
    setSelectedStaffIds(newSelection);
  };

  const handleSelectAll = () => {
    if (selectedStaffIds.size === filteredStaff.length) {
      setSelectedStaffIds(new Set());
    } else {
      setSelectedStaffIds(new Set(filteredStaff.map(staff => staff.id)));
    }
  };

  const handleBulkDelete = async () => {
    setBulkOperationInProgress(true);
    setBulkError(null);
    
    try {
      const deletePromises = Array.from(selectedStaffIds).map(async (staffId) => {
        const success = await deleteStaff(staffId);
        return { staffId, success };
      });
      
      const results = await Promise.all(deletePromises);
      const failures = results.filter(r => !r.success);
      
      if (failures.length > 0) {
        setBulkError(`${failures.length}件のスタッフの削除に失敗しました`);
      } else {
        setSelectedStaffIds(new Set());
        setBulkMode(false);
      }
    } catch (err) {
      setBulkError('バルク削除中にエラーが発生しました');
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const handleBulkStatusChange = async (newStatus: StaffStatus) => {
    setBulkOperationInProgress(true);
    setBulkError(null);
    
    try {
      const updatePromises = Array.from(selectedStaffIds).map(async (staffId) => {
        const success = await updateStaff(staffId, { 
          basicInfo: { status: newStatus } as any
        });
        return { staffId, success };
      });
      
      const results = await Promise.all(updatePromises);
      const failures = results.filter(r => !r.success);
      
      if (failures.length > 0) {
        setBulkError(`${failures.length}件のスタッフのステータス変更に失敗しました`);
      } else {
        setSelectedStaffIds(new Set());
        setBulkMode(false);
      }
    } catch (err) {
      setBulkError('バルクステータス変更中にエラーが発生しました');
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const handleBulkRoleChange = async (newRole: StaffRole) => {
    setBulkOperationInProgress(true);
    setBulkError(null);
    
    try {
      const updatePromises = Array.from(selectedStaffIds).map(async (staffId) => {
        const success = await updateStaff(staffId, { 
          basicInfo: { role: newRole } as any
        });
        return { staffId, success };
      });
      
      const results = await Promise.all(updatePromises);
      const failures = results.filter(r => !r.success);
      
      if (failures.length > 0) {
        setBulkError(`${failures.length}件のスタッフのロール変更に失敗しました`);
      } else {
        setSelectedStaffIds(new Set());
        setBulkMode(false);
      }
    } catch (err) {
      setBulkError('バルクロール変更中にエラーが発生しました');
    } finally {
      setBulkOperationInProgress(false);
    }
  };

  const clearBulkError = () => {
    setBulkError(null);
  };

  // Get role chip
  const getRoleChip = (role: StaffRole) => {
    const roleConfig = {
      owner: { label: 'オーナー', color: 'secondary' as const, icon: '👑' },
      manager: { label: 'マネージャー', color: 'primary' as const, icon: '👤' },
      staff: { label: 'スタッフ', color: 'default' as const, icon: '🧑‍💼' },
      trainee: { label: '研修生', color: 'info' as const, icon: '🎓' },
    };

    const config = roleConfig[role];
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={<span>{config.icon}</span>}
        variant="outlined"
      />
    );
  };

  // Get status chip
  const getStatusChip = (status: StaffStatus) => {
    const statusConfig = {
      active: { label: '勤務中', color: 'success' as const },
      inactive: { label: '休職中', color: 'error' as const },
      vacation: { label: '休暇中', color: 'warning' as const },
    };

    const config = statusConfig[status];
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="filled"
      />
    );
  };

  // Render loading skeleton
  const renderLoading = () => (
    <Grid container spacing={2}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Skeleton variant="circular" width={56} height={56} sx={{ mr: 2 }} />
                <Box flexGrow={1}>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                </Box>
              </Box>
              <Skeleton variant="text" width="100%" />
              <Box display="flex" gap={1} mt={1}>
                <Skeleton variant="rectangular" width={60} height={24} />
                <Skeleton variant="rectangular" width={60} height={24} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // Render card view
  const renderCardView = () => (
    <Grid container spacing={2}>
      {filteredStaff.map((staff) => (
        <Grid item xs={12} sm={6} md={4} key={staff.id}>
          <Card
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
              },
            }}
            onClick={() => handleStaffClick(staff)}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box display="flex" alignItems="center">
                  {bulkMode && (
                    <Checkbox
                      checked={selectedStaffIds.has(staff.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectStaff(staff.id, e.target.checked);
                      }}
                      sx={{ mr: 1 }}
                    />
                  )}
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      mr: 2,
                      bgcolor: 'primary.main',
                      fontSize: '1.25rem',
                    }}
                  >
                    {staff.basicInfo.lastName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {staff.basicInfo.lastName} {staff.basicInfo.firstName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {staff.basicInfo.email}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, staff.id);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>

              <Box display="flex" gap={1} mb={2}>
                {getRoleChip(staff.basicInfo.role)}
                {getStatusChip(staff.basicInfo.status)}
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  スキル・経験
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {staff.qualifications.skills.slice(0, 3).map((skill) => (
                    <Chip key={skill} label={skill} size="small" variant="outlined" />
                  ))}
                  {staff.qualifications.skills.length > 3 && (
                    <Chip
                      label={`+${staff.qualifications.skills.length - 3}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  経験年数: {staff.qualifications.experience}年
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" gap={1}>
                  <Tooltip title="電話">
                    <IconButton size="small" href={`tel:${staff.basicInfo.phone}`}>
                      <PhoneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="メール">
                    <IconButton size="small" href={`mailto:${staff.basicInfo.email}`}>
                      <EmailIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  入社: {new Date(staff.basicInfo.hireDate).toLocaleDateString('ja-JP')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // Render table view
  const renderTableView = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            {bulkMode && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedStaffIds.size === filteredStaff.length && filteredStaff.length > 0}
                  indeterminate={selectedStaffIds.size > 0 && selectedStaffIds.size < filteredStaff.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
            )}
            <TableCell>スタッフ</TableCell>
            <TableCell>連絡先</TableCell>
            <TableCell>役職・ステータス</TableCell>
            <TableCell>スキル・経験</TableCell>
            <TableCell>入社日</TableCell>
            <TableCell align="right">操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredStaff.map((staff) => (
            <TableRow
              key={staff.id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => handleStaffClick(staff)}
            >
              {bulkMode && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedStaffIds.has(staff.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSelectStaff(staff.id, e.target.checked);
                    }}
                  />
                </TableCell>
              )}
              <TableCell>
                <Box display="flex" alignItems="center">
                  <Avatar
                    sx={{ width: 40, height: 40, mr: 2, bgcolor: 'primary.main' }}
                  >
                    {staff.basicInfo.lastName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {staff.basicInfo.lastName} {staff.basicInfo.firstName}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">{staff.basicInfo.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {staff.basicInfo.phone}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  {getRoleChip(staff.basicInfo.role)}
                  {getStatusChip(staff.basicInfo.status)}
                </Box>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">
                    {staff.qualifications.skills.slice(0, 2).join(', ')}
                    {staff.qualifications.skills.length > 2 && '...'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    経験年数: {staff.qualifications.experience}年
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {new Date(staff.basicInfo.hireDate).toLocaleDateString('ja-JP')}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, staff.id);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          スタッフ一覧
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant={bulkMode ? "contained" : "outlined"}
            size="small"
            startIcon={<BulkIcon />}
            onClick={handleToggleBulkMode}
            color={bulkMode ? "primary" : "inherit"}
          >
            {bulkMode ? "選択モード終了" : "一括選択"}
          </Button>
          {!isMobile && (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="card">
                <CardViewIcon />
              </ToggleButton>
              <ToggleButton value="table">
                <ListViewIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {/* Bulk Operations Bar */}
      {bulkMode && (
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent sx={{ py: 2 }}>
            <Box display="flex" justifyContent="between" alignItems="center">
              <Box display="flex" alignItems="center" gap={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedStaffIds.size === filteredStaff.length && filteredStaff.length > 0}
                      indeterminate={selectedStaffIds.size > 0 && selectedStaffIds.size < filteredStaff.length}
                      onChange={handleSelectAll}
                    />
                  }
                  label={`${selectedStaffIds.size}件選択中`}
                />
              </Box>
              
              {selectedStaffIds.size > 0 && (
                <Box display="flex" gap={1}>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>ステータス変更</InputLabel>
                    <Select
                      label="ステータス変更"
                      defaultValue=""
                      onChange={(e) => e.target.value && handleBulkStatusChange(e.target.value as StaffStatus)}
                      disabled={bulkOperationInProgress}
                    >
                      <MenuItem value="active">勤務中</MenuItem>
                      <MenuItem value="vacation">休暇中</MenuItem>
                      <MenuItem value="inactive">休職中</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>ロール変更</InputLabel>
                    <Select
                      label="ロール変更"
                      defaultValue=""
                      onChange={(e) => e.target.value && handleBulkRoleChange(e.target.value as StaffRole)}
                      disabled={bulkOperationInProgress}
                    >
                      <MenuItem value="owner">オーナー</MenuItem>
                      <MenuItem value="manager">マネージャー</MenuItem>
                      <MenuItem value="staff">スタッフ</MenuItem>
                      <MenuItem value="trainee">研修生</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBulkDelete}
                    disabled={bulkOperationInProgress}
                  >
                    削除
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center" mb={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="スタッフを検索..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              {activeFilterCount > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  color="secondary"
                >
                  フィルタークリア ({activeFilterCount})
                </Button>
              )}
              <Badge badgeContent={activeFilterCount} color="primary">
                <Button
                  variant={filterPanelOpen ? "contained" : "outlined"}
                  size="small"
                  startIcon={<FilterIcon />}
                  endIcon={filterPanelOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setFilterPanelOpen(!filterPanelOpen)}
                >
                  フィルター
                </Button>
              </Badge>
            </Box>
          </Grid>
        </Grid>

        {/* Advanced Filter Panel */}
        <Collapse in={filterPanelOpen}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              詳細フィルター
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              {/* Role Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>役職</InputLabel>
                  <Select
                    value={filters.role || ''}
                    onChange={(e) => handleRoleFilter(e.target.value as StaffRole || null)}
                    label="役職"
                  >
                    <MenuItem value="">すべて</MenuItem>
                    <MenuItem value="owner">オーナー</MenuItem>
                    <MenuItem value="manager">マネージャー</MenuItem>
                    <MenuItem value="staff">スタッフ</MenuItem>
                    <MenuItem value="trainee">研修生</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Status Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>勤務状況</InputLabel>
                  <Select
                    value={filters.status || ''}
                    onChange={(e) => handleStatusFilter(e.target.value as StaffStatus || null)}
                    label="勤務状況"
                  >
                    <MenuItem value="">すべて</MenuItem>
                    <MenuItem value="active">勤務中</MenuItem>
                    <MenuItem value="vacation">休暇中</MenuItem>
                    <MenuItem value="inactive">休職中</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Skills Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>スキル</InputLabel>
                  <Select
                    multiple
                    value={filters.skills || []}
                    onChange={(e) => handleSkillsFilter(typeof e.target.value === 'string' ? [e.target.value] : e.target.value)}
                    input={<OutlinedInput label="スキル" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    {availableSkills.map((skill) => (
                      <MenuItem key={skill} value={skill}>
                        <Checkbox checked={(filters.skills || []).indexOf(skill) > -1} />
                        <ListItemText primary={skill} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date Availability Filter */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  type="date"
                  label="利用可能日"
                  value={filters.availableDate ? filters.availableDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleDateFilter(e.target.value ? new Date(e.target.value) : null)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DateRangeIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Time Range Filter */}
            {filters.availableDate && (
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type="time"
                      label="開始時間"
                      value={filters.availableTimeRange?.start || ''}
                      onChange={(e) => handleTimeRangeFilter({
                        start: e.target.value,
                        end: filters.availableTimeRange?.end || '17:00'
                      })}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TimeIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      size="small"
                      fullWidth
                      type="time"
                      label="終了時間"
                      value={filters.availableTimeRange?.end || ''}
                      onChange={(e) => handleTimeRangeFilter({
                        start: filters.availableTimeRange?.start || '09:00',
                        end: e.target.value
                      })}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TimeIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Filter Summary */}
            <Box mt={2}>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {filters.role && (
                  <Chip
                    label={`役職: ${
                      filters.role === 'owner' ? 'オーナー' :
                      filters.role === 'manager' ? 'マネージャー' :
                      filters.role === 'staff' ? 'スタッフ' : '研修生'
                    }`}
                    size="small"
                    onDelete={() => handleRoleFilter(null)}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {filters.status && (
                  <Chip
                    label={`状況: ${
                      filters.status === 'active' ? '勤務中' :
                      filters.status === 'vacation' ? '休暇中' : '休職中'
                    }`}
                    size="small"
                    onDelete={() => handleStatusFilter(null)}
                    color="success"
                    variant="outlined"
                  />
                )}
                {filters.skills && filters.skills.length > 0 && (
                  <Chip
                    label={`スキル: ${filters.skills.length}件`}
                    size="small"
                    onDelete={() => handleSkillsFilter([])}
                    color="info"
                    variant="outlined"
                  />
                )}
                {filters.availableDate && (
                  <Chip
                    label={`日付: ${filters.availableDate.toLocaleDateString('ja-JP')}`}
                    size="small"
                    onDelete={() => handleDateFilter(null)}
                    color="warning"
                    variant="outlined"
                  />
                )}
                {filters.availableTimeRange && (
                  <Chip
                    label={`時間: ${filters.availableTimeRange.start}-${filters.availableTimeRange.end}`}
                    size="small"
                    onDelete={() => handleTimeRangeFilter(null)}
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          </Card>
        </Collapse>
      </Box>

      {/* Error Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              システムエラーが発生しました
            </Typography>
            <Typography variant="body2">
              {error}
            </Typography>
          </Box>
        </Alert>
      )}
      
      {bulkError && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={clearBulkError}>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              バルク操作でエラーが発生しました
            </Typography>
            <Typography variant="body2">
              {bulkError}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
              onClick={() => {
                clearBulkError();
                setBulkMode(false);
                setSelectedStaffIds(new Set());
              }}
            >
              操作をリセット
            </Button>
          </Box>
        </Alert>
      )}

      {bulkOperationInProgress && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2">
              バルク操作を実行中です...
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Staff Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <PersonIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {filteredStaff.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                総スタッフ数
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <WorkIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {filteredStaff.filter(s => s.basicInfo.status === 'active').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                勤務中
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <SchoolIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {filteredStaff.filter(s => s.basicInfo.role === 'trainee').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                研修生
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <StarIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" fontWeight="bold">
                {Math.round(
                  filteredStaff.reduce((sum, s) => sum + s.qualifications.experience, 0) /
                  Math.max(filteredStaff.length, 1) * 10
                ) / 10}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                平均経験年数
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Staff List */}
      {isLoading ? renderLoading() : (
        <>
          {filteredStaff.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 8 }}>
                <PersonIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchQuery ? '検索結果が見つかりません' : 'スタッフが登録されていません'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {searchQuery 
                    ? '検索条件を変更してお試しください' 
                    : '新しいスタッフを追加して始めましょう'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddStaff}
                >
                  新規スタッフ追加
                </Button>
              </CardContent>
            </Card>
          ) : (
            viewMode === 'card' ? renderCardView() : renderTableView()
          )}
        </>
      )}

      {/* Add Staff FAB */}
      {filteredStaff.length > 0 && (
        <Fab
          color="primary"
          aria-label="add staff"
          onClick={handleAddStaff}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            const staff = filteredStaff.find(s => s.id === menuStaffId);
            if (staff) handleEditStaff(staff);
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          編集
        </MenuItem>
        <MenuItem
          onClick={() => {
            const staff = filteredStaff.find(s => s.id === menuStaffId);
            if (staff) handleDeleteStaff(staff);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          削除
        </MenuItem>
      </Menu>

      {/* Staff Editor Dialog */}
      <StaffEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        staff={selectedStaff}
        onSave={handleStaffSave}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>スタッフ削除の確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {staffToDelete && (
              <>
                「{staffToDelete.basicInfo.lastName} {staffToDelete.basicInfo.firstName}」を削除しますか？
                <br />
                この操作は取り消すことができません。
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            キャンセル
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffListView;